import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { WbButton } from "./wb";
import {
  cameraCall,
  CameraApiError,
  stationFresh,
  statusText,
  type Station,
  type Recording,
} from "../lib/camera/client";

/** Scope stays at the selected WI. Never derive a recording's target from current UI on stop/upload. */
export function RemoteCameraPanel({
  wiSfId,
  wiName,
  onAvailable,
}: {
  wiSfId?: string;
  wiName?: string;
  onAvailable?: (available: boolean) => void;
}) {
  const [stations, setStations] = useState<Station[]>([]);
  const [active, setActive] = useState<Recording[]>([]);
  const [history, setHistory] = useState<Recording[]>([]);
  const [selected, setSelected] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState("");
  const [lastSuccess, setLastSuccess] = useState(0);
  const [busy, setBusy] = useState(false);
  const [play, setPlay] = useState<{ id: string; url: string } | null>(null);
  const request = useRef<{
    id: string;
    stationId: string;
    wiSfId: string;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    let polling = true;
    setHistory([]);
    setPlay(null);
    request.current = null;
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const reply = await cameraCall("list");
        if (cancelled) return;
        setEnabled(true);
        onAvailable?.(true);
        setStations(reply.stations || []);
        setActive(reply.active || []);
        setLastSuccess(Date.now());
        if (wiSfId) {
          const h = await cameraCall("history", { wiSfId });
          if (!cancelled) setHistory(h.recordings || []);
        }
      } catch (e) {
        if (!cancelled) {
          setLastSuccess(0);
          if (e instanceof CameraApiError && (e.status === 401 || e.status === 403 || e.message === "Cameraproef staat uit")) {
            polling = false;
            setEnabled(false);
            onAvailable?.(false);
          }
        }
      } finally {
        if (!cancelled && polling) timer = setTimeout(() => void poll(), 2500);
      }
    }
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [wiSfId, onAvailable]);
  const station = stations.find((s) => s.id === selected) || stations[0];
  const current = active.find((r) => r.station_id === station?.id);
  const fresh =
    !!station && stationFresh(station) && Date.now() - lastSuccess < 12000;
  async function start() {
    if (!station || !wiSfId || busy) return;
    setBusy(true);
    setError("");
    if (
      !request.current ||
      request.current.stationId !== station.id ||
      request.current.wiSfId !== wiSfId
    ) {
      request.current = {
        id: crypto.randomUUID(),
        stationId: station.id,
        wiSfId,
      };
    }
    try {
      const reply = await cameraCall("start", request.current);
      if (reply.recording)
        setActive((a) => [
          ...a.filter((r) => r.station_id !== station.id),
          reply.recording!,
        ]);
      // Keep the id on uncertain response. On confirmed result a next deliberate take gets a new id.
      request.current = null;
    } catch (e) {
      setError(
        `${e instanceof Error ? e.message : "Start niet bevestigd"} Controleer de status vóór opnieuw proberen.`,
      );
    } finally {
      setBusy(false);
    }
  }
  async function stop() {
    if (!current || busy) return;
    setBusy(true);
    setError("");
    try {
      await cameraCall("stop", { id: current.id });
      setActive((a) =>
        a.map((r) =>
          r.id === current.id ? { ...r, status: "stop_requested" } : r,
        ),
      );
    } catch {
      setError(
        "Stop niet bevestigd. Opname kan nog lopen. Controleer de iPhone.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function playback(id: string) {
    try {
      const r = await cameraCall("play", { id });
      if (r.url) setPlay({ id, url: r.url });
    } catch {
      setError(
        "Afspelen lukt niet. Probeer opnieuw voor een nieuw tijdelijk afspeeladres.",
      );
    }
  }
  if (!enabled) return null; // Server flag + allowlist are authoritative; existing workflow unchanged.
  return (
    <section className="space-y-3 border-2 border-wb-rand rounded-lg p-3">
      <h2 className="font-bold text-lg">Vaste wasserijcamera</h2>
      <Link to="/camera-station" className="underline block py-2">
        Op de iPhone: camera boven instellen
      </Link>
      {!wiSfId && (
        <p>Kies hieronder een kleed om vanaf deze tablet te filmen.</p>
      )}
      {wiSfId && (
        <>
          <p className="font-bold">Opnemen voor {wiName || wiSfId}</p>
          {stations.length > 1 && (
            <label>
              Camera
              <select
                className="block p-3 border rounded"
                value={station?.id || ""}
                onChange={(e) => setSelected(e.target.value)}
              >
                {stations.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} · {s.id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!station && <p>Geen camera gekoppeld. Activeer eerst de iPhone.</p>}
          {station && (
            <>
              <p>
                {station.name} · {station.id.slice(0, 8)} ·{" "}
                {fresh
                  ? "Verbinding recent bevestigd"
                  : "Verbinding weg of verouderd; opnamestatus onbekend"}
              </p>
              {fresh && station.preview && (
                <img
                  src={station.preview}
                  alt="Recente camera-foto, geen vloeiend livebeeld"
                  className="max-h-80 w-full object-contain bg-black"
                />
              )}
              <p className="text-sm">
                Voorvertoning: circa elke 2 seconden. Camera:{" "}
                {String(station.settings.width || "?")} ×{" "}
                {String(station.settings.height || "?")} ·{" "}
                {String(station.settings.frameRate || "?")} fps
              </p>
              {current && (
                <p role="status" className="font-bold">
                  {statusText[current.status]} · {current.wi_name}
                  {current.started_at
                    ? ` · ${Math.max(0, Math.floor((Date.now() - Date.parse(current.started_at)) / 1000))} s sinds start`
                    : ""}
                </p>
              )}
              <div className="flex gap-3 flex-wrap">
                <WbButton
                  disabled={busy || !fresh || !station.ready || !!current}
                  onClick={() => void start()}
                >
                  Opnemen voor {wiName || "dit kleed"}
                </WbButton>
                {current &&
                  ["queued", "recording", "stop_requested"].includes(
                    current.status,
                  ) && (
                    <WbButton
                      variant="secondary"
                      disabled={busy}
                      onClick={() => void stop()}
                    >
                      Stop opname {current.wi_name}
                    </WbButton>
                  )}
              </div>
            </>
          )}
          {error && (
            <p role="alert" className="font-bold text-red-700">
              {error}
            </p>
          )}
          <h3 className="font-bold">Opnames bij dit kleed</h3>
          {history.length === 0 && <p>Nog geen opnames met de vaste camera.</p>}
          {history.map((r) => (
            <div key={r.id} className="border-t pt-2">
              <p>
                {new Date(r.created_at).toLocaleString("nl-NL")} ·{" "}
                {r.status === "saved" && r.media_id ? statusText.promoted : statusText[r.status]}
                {r.interrupted ? " · Onderbroken: controle nodig" : ""}
              </p>
              {r.detail && <p>{r.detail}</p>}
              {r.status === "saved" && (
                <WbButton
                  variant="secondary"
                  onClick={() => void playback(r.id)}
                >
                  Opname bekijken
                </WbButton>
              )}
              {play?.id === r.id && (
                <video
                  src={play.url}
                  controls
                  playsInline
                  className="w-full max-h-96"
                />
              )}
            </div>
          ))}
        </>
      )}
      <p className="text-sm">
        Maximaal 2,5 minuut per opname. Een opgeslagen opname komt als
        wasvideo bij het kleed en kan door het social-systeem als Reel worden
        voorgesteld; publiceren gebeurt pas na akkoord in de office-app. Sluit
        de camerapagina pas als de upload bevestigd is.
      </p>
    </section>
  );
}
